/**
 * Powers the "Shop the Look" carousel: highlights the centered slide,
 * wires the prev/next nav arrows, and keeps a progress bar in sync with
 * scroll position. Pure enhancement — the track works as a native
 * horizontally-scrollable/snappable list even without this script.
 */
class ShopTheLookComponent extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-role="track"]');
    this.slides = Array.from(this.querySelectorAll('[data-role="slide"]'));
    this.prevBtn = this.querySelector('[data-role="prev"]');
    this.nextBtn = this.querySelector('[data-role="next"]');
    this.progressFill = this.querySelector('[data-role="progress-fill"]');

    if (!this.track || this.slides.length === 0) return;

    this.#setupActiveObserver();
    this.#setupNavButtons();
    this.#setupProgressBar();
  }

  #setupActiveObserver() {
    if (!('IntersectionObserver' in window)) {
      this.slides[0]?.classList.add('is-active');
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          entry.target.classList.toggle('is-active', entry.isIntersecting && entry.intersectionRatio > 0.6);
        });
      },
      { root: this.track, threshold: [0, 0.6, 1] }
    );

    this.slides.forEach((slide) => observer.observe(slide));
  }

  #setupNavButtons() {
    const scrollByAmount = () => {
      const slide = this.slides[0];
      const gap = 16;
      return (slide?.getBoundingClientRect().width || 280) + gap;
    };

    this.prevBtn?.addEventListener('click', () => {
      this.track.scrollBy({ left: -scrollByAmount(), behavior: 'smooth' });
    });

    this.nextBtn?.addEventListener('click', () => {
      this.track.scrollBy({ left: scrollByAmount(), behavior: 'smooth' });
    });
  }

  #setupProgressBar() {
    if (!this.progressFill) return;

    const updateProgress = () => {
      const maxScroll = this.track.scrollWidth - this.track.clientWidth;
      const ratio = maxScroll > 0 ? this.track.scrollLeft / maxScroll : 0;

      const fillWidth = 100 / this.slides.length;
      const travel = 100 - fillWidth;

      this.progressFill.style.width = `${fillWidth}%`;
      this.progressFill.style.left = `${ratio * travel}%`;
    };

    this.track.addEventListener('scroll', updateProgress, { passive: true });
    updateProgress();
  }
}

if (!customElements.get('shop-the-look-component')) {
  customElements.define('shop-the-look-component', ShopTheLookComponent);
}
